import { InputsForm1 } from "../types/InputsForm1";
import { EMAIL_PATTERN } from "./normalizeEmail";

/**
 * Form1の必須項目バリデーション
 * @param form1 - Form1のデータ
 * @param languageCode - 言語コード（"ja" or "tw"）
 * @returns エラーメッセージの配列（エラーがない場合は空配列）
 */
export const validateForm1 = (form1: InputsForm1, languageCode: string = "ja"): string[] => {
  const errors: string[] = [];
  const isJapanese = languageCode === "ja";

  // 個人情報取扱同意
  if (!form1.agree || form1.agree === "") {
    errors.push("- 個人情報取扱について同意してください");
  }

  // 申込者: 氏名（漢字 or アルファベット）
  if (!form1.applicant.name.kanji.last || form1.applicant.name.kanji.last.trim() === "") {
    errors.push("- 申込者の氏名（姓）を入力してください");
  } else if (!isJapanese && !/^[a-zA-Z]+$/.test(form1.applicant.name.kanji.last)) {
    // TWの場合はアルファベットのみ
    errors.push("- 申込者の氏名（姓）はアルファベットで入力してください");
  }

  if (!form1.applicant.name.kanji.first || form1.applicant.name.kanji.first.trim() === "") {
    errors.push("- 申込者の氏名（名）を入力してください");
  } else if (!isJapanese && !/^[a-zA-Z]+$/.test(form1.applicant.name.kanji.first)) {
    // TWの場合はアルファベットのみ
    errors.push("- 申込者の氏名（名）はアルファベットで入力してください");
  }

  // 申込者: 氏名（カナ）- 日本語の場合のみ必須
  if (isJapanese) {
    if (!form1.applicant.name.kana.last || form1.applicant.name.kana.last.trim() === "") {
      errors.push("- 申込者の氏名（セイ）を入力してください");
    } else if (!/^[ァ-ンヴー]+$/i.test(form1.applicant.name.kana.last)) {
      errors.push("- 申込者の氏名（セイ）は全角カタカナで入力してください");
    }

    if (!form1.applicant.name.kana.first || form1.applicant.name.kana.first.trim() === "") {
      errors.push("- 申込者の氏名（メイ）を入力してください");
    } else if (!/^[ァ-ンヴー]+$/i.test(form1.applicant.name.kana.first)) {
      errors.push("- 申込者の氏名（メイ）は全角カタカナで入力してください");
    }
  }

  // 申込者: 生年月日（全部入力されているか、全部空か）
  const hasYear = form1.applicant.birthday_year && form1.applicant.birthday_year.trim() !== "";
  const hasMonth = form1.applicant.birthday_month && form1.applicant.birthday_month.trim() !== "";
  const hasDay = form1.applicant.birthday_day && form1.applicant.birthday_day.trim() !== "";

  if ((hasYear || hasMonth || hasDay) && !(hasYear && hasMonth && hasDay)) {
    errors.push("- 申込者の生年月日を正しくご入力ください");
  }

  // 申込者: 性別
  if (!form1.applicant.gender || form1.applicant.gender === "") {
    errors.push("- 申込者の性別を選択してください");
  }

  // 申込者: メールアドレス
  if (!form1.applicant.email || form1.applicant.email.trim() === "") {
    errors.push("- メールアドレスを入力してください");
  } else if (!EMAIL_PATTERN.test(form1.applicant.email)) {
    errors.push("- メールアドレスの形式が正しくありません");
  }

  // 申込者: メールアドレス（確認）
  if (!form1.applicant.email_confirm || form1.applicant.email_confirm.trim() === "") {
    errors.push("- メールアドレス（確認）を入力してください");
  } else if (form1.applicant.email !== form1.applicant.email_confirm) {
    errors.push("- メールアドレスが一致しません");
  }

  // 申込者: 携帯番号
  if (!form1.applicant.tel || form1.applicant.tel.trim() === "") {
    errors.push("- 携帯番号を入力してください");
  } else if (!/^[0-9]+$/i.test(form1.applicant.tel)) {
    errors.push("- 携帯番号の形式が正しくありません");
  }

  // 緊急連絡先（任意だが、入力されている場合は形式チェック）
  if (form1.applicant.emergency_tel && form1.applicant.emergency_tel.trim() !== "") {
    if (!/^[0-9]+$/i.test(form1.applicant.emergency_tel)) {
      errors.push("- 緊急連絡先の電話番号形式が正しくありません");
    }
  }

  // 代表者情報: 旅行参加の有無
  if (!form1.representative.is_applicant_join || form1.representative.is_applicant_join === "") {
    errors.push("- 申込者のご旅行参加の有無を選択してください");
  }

  // 代表者が別途いる場合（is_applicant_join = "no"）
  if (form1.representative.is_applicant_join === "no") {
    // 代表者: 氏名（漢字 or アルファベット）
    if (!form1.representative.name.kanji.last || form1.representative.name.kanji.last.trim() === "") {
      errors.push("- 旅行の代表者様の氏名（姓）を入力してください");
    } else if (!isJapanese && !/^[a-zA-Z]+$/.test(form1.representative.name.kanji.last)) {
      // TWの場合はアルファベットのみ
      errors.push("- 旅行の代表者様の氏名（姓）はアルファベットで入力してください");
    }

    if (!form1.representative.name.kanji.first || form1.representative.name.kanji.first.trim() === "") {
      errors.push("- 旅行の代表者様の氏名（名）を入力してください");
    } else if (!isJapanese && !/^[a-zA-Z]+$/.test(form1.representative.name.kanji.first)) {
      // TWの場合はアルファベットのみ
      errors.push("- 旅行の代表者様の氏名（名）はアルファベットで入力してください");
    }

    // 代表者: 氏名（カナ）- 日本語の場合のみ必須
    if (isJapanese) {
      if (!form1.representative.name.kana.last || form1.representative.name.kana.last.trim() === "") {
        errors.push("- 旅行の代表者様の氏名（セイ）を入力してください");
      } else if (!/^[ァ-ンヴー]+$/i.test(form1.representative.name.kana.last)) {
        errors.push("- 旅行の代表者様の氏名（セイ）は全角カタカナで入力してください");
      }

      if (!form1.representative.name.kana.first || form1.representative.name.kana.first.trim() === "") {
        errors.push("- 旅行の代表者様の氏名（メイ）を入力してください");
      } else if (!/^[ァ-ンヴー]+$/i.test(form1.representative.name.kana.first)) {
        errors.push("- 旅行の代表者様の氏名（メイ）は全角カタカナで入力してください");
      }
    }

    // 代表者: 性別
    if (!form1.representative.gender || form1.representative.gender === "") {
      errors.push("- 旅行の代表者様の性別を選択してください");
    }

    // 代表者: 生年月日（全部入力されているか、全部空か）
    const repHasYear = form1.representative.birthday_year && form1.representative.birthday_year.trim() !== "";
    const repHasMonth = form1.representative.birthday_month && form1.representative.birthday_month.trim() !== "";
    const repHasDay = form1.representative.birthday_day && form1.representative.birthday_day.trim() !== "";

    if ((repHasYear || repHasMonth || repHasDay) && !(repHasYear && repHasMonth && repHasDay)) {
      errors.push("- 旅行の代表者様の生年月日を正しくご入力ください");
    }

    // 代表者: 携帯番号
    if (!form1.representative.tel || form1.representative.tel.trim() === "") {
      errors.push("- 旅行の代表者様の携帯番号を入力してください");
    } else if (!/^[0-9]+$/i.test(form1.representative.tel)) {
      errors.push("- 旅行の代表者様の携帯番号形式が正しくありません");
    }
  }

  // 同行者のバリデーション
  if (form1.followers && form1.followers.length > 0) {
    form1.followers.forEach((follower, index) => {
      const followerNum = index + 1;

      // 同行者: 氏名（カナ）
      if (!follower.name.kana.last || follower.name.kana.last.trim() === "") {
        errors.push(`- 同行者${followerNum}の氏名（セイ）を入力してください`);
      } else if (!/^[ァ-ンヴー]+$/i.test(follower.name.kana.last)) {
        errors.push(`- 同行者${followerNum}の氏名（セイ）は全角カタカナで入力してください`);
      }

      if (!follower.name.kana.first || follower.name.kana.first.trim() === "") {
        errors.push(`- 同行者${followerNum}の氏名（メイ）を入力してください`);
      } else if (!/^[ァ-ンヴー]+$/i.test(follower.name.kana.first)) {
        errors.push(`- 同行者${followerNum}の氏名（メイ）は全角カタカナで入力してください`);
      }

      // 同行者: 性別
      if (!follower.gender || follower.gender === "") {
        errors.push(`- 同行者${followerNum}の性別を選択してください`);
      }

      // 同行者: 生年月日（必須）
      if (!follower.birthday_year || !follower.birthday_month || !follower.birthday_day) {
        errors.push(`- 同行者${followerNum}の生年月日を入力してください`);
      }
    });
  }

  return errors;
};
